import React, { Fragment, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, CheckCircle, Eye, Minus, CaretDown, CaretRight } from "@phosphor-icons/react";

const Cell = ({ c }) => {
  if (c.passed) return <CheckCircle size={15} weight="fill" className="text-emerald-500" />;
  if (c.watched) return <Eye size={15} className="text-amber-500" />;
  return <Minus size={13} className="text-muted-foreground/50" />;
};

const Bar = ({ pct, done }) => (
  <div className="flex items-center gap-1.5">
    <div className="h-1.5 rounded-full bg-muted overflow-hidden flex-1 min-w-[48px]">
      <div className={`h-full ${done ? "bg-emerald-500" : pct >= 50 ? "bg-primary" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
    </div>
    <span className="text-[10px] font-mono w-9 text-right">{pct}%</span>
  </div>
);

const Detail = ({ r, videos }) => (
  <tr className="bg-muted/20" data-testid={`team-training-detail-${r.user_id}`}>
    <td colSpan={4} className="px-3 py-2">
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px]">
        {r.cells.map((c) => {
          const v = videos.find((x) => x.id === c.video_id);
          return (
            <span key={c.video_id} className="inline-flex items-center gap-1" title={`${c.attempts} intento(s)`}>
              <Cell c={c} /> {v?.short} {c.attempts > 0 && <span className="text-muted-foreground">· {c.attempts} int.</span>}
            </span>
          );
        })}
      </div>
    </td>
  </tr>
);

export const TeamTrainingPanel = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    api.get("/training/videos/team-progress")
      .then((v) => {
        const rows = (v.data.rows || []).map((r) => ({ ...r, ready: r.passed_count === r.total }))
          .sort((a, b) => (b.passed_count - a.passed_count) || a.name.localeCompare(b.name));
        setData({ videos: v.data.videos, rows });
      }).catch(() => {});
  }, []);
  if (!data?.rows?.length) return null;
  const ready = data.rows.filter((r) => r.ready).length;

  return (
    <div className="rounded-lg border border-border bg-card" data-testid="team-training-panel">
      <button onClick={() => setCollapsed((x) => !x)} className="w-full flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors" data-testid="team-training-toggle">
        <div className="h-9 w-9 rounded-lg bg-emerald-500/15 text-emerald-600 flex items-center justify-center shrink-0"><GraduationCap size={18} weight="fill" /></div>
        <div className="flex-1 min-w-0 text-left">
          <div className="font-heading font-bold text-sm">Entrenamiento del equipo</div>
          <div className="text-[11px] text-muted-foreground" data-testid="team-training-ready">
            {ready} / {data.rows.length} agentes listos para su turno · Listo = {data.videos?.length || 6} videos de Agente aprobados
          </div>
        </div>
        <CaretDown size={14} className={`transition-transform ${collapsed ? "" : "rotate-180"}`} />
      </button>
      {!collapsed && (
        <div className="border-t border-border max-h-[320px] overflow-auto">
          <table className="w-full text-[11px]">
            <thead className="text-[10px] uppercase text-muted-foreground bg-muted/30 sticky top-0 z-10">
              <tr>
                <th className="text-left p-2 pl-3">Agente</th>
                <th className="text-left p-2 w-40">Videos aprobados</th>
                <th className="text-left p-2">Estado</th>
                <th className="w-6" />
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <Fragment key={r.user_id}>
                  <tr onClick={() => setOpen(open === r.user_id ? null : r.user_id)} className="border-t border-border/40 cursor-pointer hover:bg-muted/30"
                    data-testid={`team-training-row-${r.user_id}`}>
                    <td className="p-2 pl-3 font-medium truncate max-w-[200px]">{r.name}</td>
                    <td className="p-2"><Bar pct={Math.round((100 * r.passed_count) / r.total)} done={r.passed_count === r.total} /></td>
                    <td className="p-2">
                      {r.ready
                        ? <Badge className="text-[9px] bg-emerald-500/15 text-emerald-700 border-transparent">Listo</Badge>
                        : <Badge variant="outline" className="text-[9px] text-amber-700 border-amber-500/40">Falta</Badge>}
                    </td>
                    <td className="p-2 pr-3 text-muted-foreground">{open === r.user_id ? <CaretDown size={12} /> : <CaretRight size={12} />}</td>
                  </tr>
                  {open === r.user_id && <Detail r={r} videos={data.videos} />}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
